// client/src/components/pages/LexiStar/Replay/IdleHeatmap.tsx
//
// Idling heatmap — the second tab of Replay (ST-00188 M4-D; LS-03 FR-RPL-007;
// LS-09 §5.6; m4-contracts §4 GET /api/lexistar/idle-heatmap, §8).
//
//  - Period (shared date-range picker), truck and route filters, and the
//    "exclude permitted zones" toggle (idle inside source, destination,
//    weighbridge and delay geofences is expected; FR-RPL-007 keeps it
//    separable). All of it lives in the URL, in the scheme the metric pack's
//    drill links use (M4-A: /lexistar/replay?tab=idle&from&to&assets=<asset
//    scope id>[&routes=<route id>][&exclude_permitted=true]); from / to here are
//    SAST days, so they never read as the replay tab's ISO window (which also
//    needs asset_key). GET /idle-heatmap takes `routes` (and `drivers`) as id
//    lists; the page picks one route.
//  - Grid cells (0.002° ≈ 200 m, aggregated server-side) as squares on the
//    shared LexiStar map, coloured by idle hours on one sequential ramp in the
//    idle hue; a legend states the buckets. Hovering a cell reads it out;
//    clicking selects it.
//  - The API's ranked hotspot table: idle hours, visits, estimated litres and
//    rand (estimated everywhere, LS-04 §11). Clicking a hotspot centres the
//    map on it and outlines its cell.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { Map as MaplibreMap } from 'maplibre-gl';
import { FaFire, FaCrosshairs } from 'react-icons/fa';
import { listAssets } from '../../../../services/lexistarAssets';
import { listRoutes } from '../../../../services/lexistarRoutes';
import { getIdleHeatmap } from '../../../../services/lexistarAnalytics';
import type { IdleHeatmapResponse } from '../../../../types/lexistar-analytics';
import { LexiStarMap, ThemedSelect, UnifiedDateRangePicker } from '../../../../types/commonComponents';
import { cardClasses, ProvenanceMark, SkeletonBlock } from '../Dashboard/analyticsAtoms';
import { type Period, addDays, dayString, isDay, parseDay, periodLabel, todayString } from '../Dashboard/periodLogic';
import {
  type Heatmap,
  type HeatmapSummary,
  type HeatCell,
  type Hotspot,
  HEAT_RAMP,
  HEAT_OUTLINE,
  CELL_DEG,
  SQUARES_FROM_ZOOM,
  cellFeatures,
  cellPoints,
  cellRing,
  cellsBounds,
  estimateNote,
  heatBreaks,
  legendLabels,
  normaliseHeatmap,
  snap,
} from './idleHeatmapLogic';

const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.0, zoom: 6 };
const DEFAULT_DAYS = 7;

interface FilterOption {
  value: string;
  label: string;
}

const num = (v: number | null, digits = 0): string =>
  v === null ? '—' : v.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const hours = (h: number): string => num(h, h < 100 ? 1 : 0);

/** from / to (SAST days) → Period (default: the last seven days). */
export function readHeatPeriod(params: URLSearchParams, today: string = todayString()): Period {
  const from = params.get('from');
  const to = params.get('to');
  if (isDay(from) && isDay(to)) return from <= to ? { from, to } : { from: to, to: from };
  return { from: addDays(today, -(DEFAULT_DAYS - 1)), to: today };
}

type Loose = Record<string, unknown>;
const pick = (res: unknown, key: string): Loose[] => {
  if (Array.isArray(res)) return res as Loose[];
  const v = res && typeof res === 'object' ? (res as Loose)[key] : null;
  return Array.isArray(v) ? (v as Loose[]) : [];
};
const plate = (key: string): string => key.replace(/^(reg|asset):/, '');

/** "Permitted zone · JX12KLGP, HB45ZNGP +3" */
const hotspotNote = (h: Hotspot): string => {
  const parts: string[] = [];
  if (h.permitted) parts.push('Permitted zone');
  if (h.asset_keys.length) {
    const shown = h.asset_keys.slice(0, 2).map(plate).join(', ');
    parts.push(shown + (h.asset_keys.length > 2 ? ` +${h.asset_keys.length - 2}` : ''));
  } else if (h.assets) parts.push(`${h.assets} truck${h.assets === 1 ? '' : 's'}`);
  return parts.join(' · ');
};

/** "18 – 24 Sep 2026 · 412.5 idle hours in 1,284 cells · 38 h inside permitted zones left out · ≈ 1,650 L, R38,200" */
export function statusLine(period: Period, s: HeatmapSummary): string {
  const parts = [`${periodLabel(period)}`, `${hours(s.idle_h)} idle hours in ${num(s.cells)} cells`];
  if (s.exclude_permitted && s.permitted_idle_h > 0) parts.push(`${hours(s.permitted_idle_h)} h inside permitted zones left out`);
  if (!s.exclude_permitted && s.permitted_idle_h > 0) parts.push(`${hours(s.permitted_idle_h)} h of it inside permitted zones`);
  if (s.est_litres !== null) parts.push(`≈ ${num(s.est_litres)} L${s.est_rand !== null ? `, R${num(s.est_rand)}` : ''} (estimated)`);
  return parts.join(' · ');
}

const text = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

export default function IdleHeatmap() {
  const [params, setParams] = useSearchParams();
  const period = readHeatPeriod(params);
  const asset = params.get('assets') || '';
  const route = params.get('routes') || '';
  const excludePermitted = params.get('exclude_permitted') === 'true';

  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );
  const setPeriod = (p: Period) => setParam({ from: p.from, to: p.to });

  const [assets, setAssets] = useState<FilterOption[]>([]);
  useEffect(() => {
    let alive = true;
    listAssets({ limit: 500 })
      .then((res: unknown) => {
        if (!alive) return;
        setAssets(
          pick(res, 'assets')
            .filter((a) => a.asset_type !== 'trailer')
            .map((a) => ({
              // The asset scope id of the analytics API: the registered asset's uuid.
              value: a.id ? text(a.id) : `reg:${text(a.registration_normalised || a.registration)}`,
              label: text(a.registration) + (a.fleet_no ? ` · ${text(a.fleet_no)}` : ''),
            }))
        );
      })
      .catch(() => alive && setAssets([]));
    return () => {
      alive = false;
    };
  }, []);

  const [routes, setRoutes] = useState<FilterOption[]>([]);
  useEffect(() => {
    let alive = true;
    listRoutes()
      .then((res: unknown) => {
        if (!alive) return;
        setRoutes(
          pick(res, 'routes')
            .filter((r) => r.id)
            .map((r) => ({
              value: text(r.id),
              label: r.code && r.name ? `${text(r.code)} · ${text(r.name)}` : text(r.name || r.code || r.id),
            }))
        );
      })
      .catch(() => alive && setRoutes([]));
    return () => {
      alive = false;
    };
  }, []);

  const [data, setData] = useState<Heatmap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryKey = `${period.from}|${period.to}|${asset}|${route}|${excludePermitted}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getIdleHeatmap({
      from: period.from,
      to: period.to,
      assets: asset || undefined,
      routes: route || undefined,
      exclude_permitted: excludePermitted,
    })
      .then((res: IdleHeatmapResponse) => alive && setData(normaliseHeatmap(res)))
      .catch((err: { response?: { data?: { error?: string } }; message?: string }) => {
        if (!alive) return;
        setData(null);
        setError(err.response?.data?.error || err.message || 'The heatmap could not be loaded.');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // queryKey carries every input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  const breaks = useMemo(() => heatBreaks((data?.cells || []).map((c) => c.idle_h)), [data]);
  const cellsFC = useMemo(() => cellFeatures(data?.cells || [], breaks), [data, breaks]);
  const pointsFC = useMemo(() => cellPoints(data?.cells || [], breaks), [data, breaks]);
  // Rank badges for the top five, #1 drawn last so it sits on top where they crowd.
  const badges = useMemo(() => (data?.hotspots || []).slice(0, 5).reverse(), [data]);
  const labels = useMemo(() => legendLabels(breaks), [breaks]);
  const cellsById = useMemo(() => new Map((data?.cells || []).map((c) => [c.id, c])), [data]);

  // ── Map ────────────────────────────────────────────────────────────────────
  const mapRef = useRef<MaplibreMap | null>(null);
  const fittedKey = useRef<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null); // hotspot id or cell id
  const [hover, setHover] = useState<HeatCell | null>(null);

  const bounds = useMemo(() => cellsBounds(data?.cells || []), [data]);
  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds, { padding: 40, duration: 0, maxZoom: 14 });
    } catch {
      /* map not ready */
    }
  }, [bounds]);
  useEffect(() => {
    if (!data || fittedKey.current === queryKey) return;
    fittedKey.current = queryKey;
    setSelected(null);
    fit();
  }, [data, queryKey, fit]);

  const selectedHotspot: Hotspot | null = useMemo(
    () => data?.hotspots.find((h) => h.id === selected) || null,
    [data, selected]
  );
  const selectedCell: Pick<HeatCell, 'lat' | 'lon' | 'size'> | null = useMemo(() => {
    if (!selected) return null;
    const direct = cellsById.get(selected);
    if (direct) return direct;
    if (selectedHotspot) {
      const c = selectedHotspot.cell_id ? cellsById.get(selectedHotspot.cell_id) : undefined;
      return c || { lat: snap(selectedHotspot.lat), lon: snap(selectedHotspot.lon), size: CELL_DEG };
    }
    return null;
  }, [selected, selectedHotspot, cellsById]);
  const selectedFC = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: selectedCell
        ? [
            {
              type: 'Feature' as const,
              properties: {},
              geometry: { type: 'Polygon' as const, coordinates: [cellRing(selectedCell)] },
            },
          ]
        : [],
    }),
    [selectedCell]
  );

  const centreOn = (h: Hotspot) => {
    setSelected(h.id);
    const map = mapRef.current;
    if (!map) return;
    try {
      map.flyTo({ center: [h.lon, h.lat], zoom: Math.max(map.getZoom(), 14), duration: 600 });
    } catch {
      /* map not ready */
    }
  };

  const onMapClick = (e: MapLayerMouseEvent) => {
    const id = e.features?.[0]?.properties?.id;
    if (typeof id !== 'string') return;
    const spot = data?.hotspots.find((h) => h.cell_id === id);
    setSelected(spot ? spot.id : id);
  };
  const onMouseMove = (e: MapLayerMouseEvent) => {
    const id = e.features?.[0]?.properties?.id;
    setHover(typeof id === 'string' ? cellsById.get(id) || null : null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const assetOptions =
    asset && !assets.some((a) => a.value === asset) ? [{ value: asset, label: asset.replace(/^reg:/, '') }, ...assets] : assets;
  // A route from a link that is not in the list (not visible, or retired) still shows.
  const routeOptions = route && !routes.some((r) => r.value === route) ? [{ value: route, label: route }, ...routes] : routes;
  const s = data?.summary;
  const selectedCellData =
    (selected && (cellsById.get(selected) || (selectedHotspot ? cellsById.get(selectedHotspot.cell_id) : undefined))) || null;
  const hoveredOrSelected = hover || selectedCellData;

  return (
    <div className="space-y-4">
      <section className={`${cardClasses} p-4`} aria-label="Heatmap filters">
        <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-end gap-3">
          <div className="w-full lg:w-64">
            <span className="block text-xs font-medium text-slate-600 mb-1">Period</span>
            <UnifiedDateRangePicker
              startDate={parseDay(period.from)}
              endDate={parseDay(period.to)}
              onStartDateChange={(d) => {
                if (!d) return;
                const from = dayString(d);
                setPeriod({ from, to: period.to < from ? from : period.to });
              }}
              onEndDateChange={(d) => {
                if (!d) return;
                const to = dayString(d);
                setPeriod({ from: period.from > to ? to : period.from, to });
              }}
            />
          </div>
          <div className="w-full lg:w-60">
            <label htmlFor="heat-asset" className="block text-xs font-medium text-slate-600 mb-1">
              Truck
            </label>
            <ThemedSelect
              id="heat-asset"
              aria-label="Truck"
              value={asset}
              isClearable
              onChange={(e) => setParam({ assets: e.target.value || null })}
              className="text-sm"
            >
              <option value="">All trucks</option>
              {assetOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <div className="w-full lg:w-60">
            <label htmlFor="heat-route" className="block text-xs font-medium text-slate-600 mb-1">
              Route
            </label>
            <ThemedSelect
              id="heat-route"
              aria-label="Route"
              value={route}
              isClearable
              onChange={(e) => setParam({ routes: e.target.value || null })}
              className="text-sm"
            >
              <option value="">All routes</option>
              {routeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <label className="inline-flex items-center gap-2.5 min-h-[40px] cursor-pointer select-none lg:ml-auto">
            <input
              type="checkbox"
              role="switch"
              checked={excludePermitted}
              onChange={(e) => setParam({ exclude_permitted: e.target.checked ? 'true' : null })}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className="relative inline-block w-9 h-5 rounded-full bg-slate-300 transition-colors peer-checked:bg-primary-500 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-4"
            />
            <span className="text-sm text-slate-800">
              Exclude permitted zones
              <span className="block text-[11px] text-slate-500">Source, destination, weighbridge and delay geofences</span>
            </span>
          </label>
        </div>
      </section>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
          {error}
        </div>
      )}

      <p className="text-xs text-slate-600 tabular-nums" role="status" aria-live="polite">
        {loading ? 'Loading idle time…' : data ? statusLine(period, data.summary) : ''}
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-8 min-w-0">
          <div className="relative z-0 h-[360px] lg:h-[520px] rounded-xl overflow-hidden border border-slate-200">
            <LexiStarMap
              className="absolute inset-0"
              initialViewState={DEFAULT_VIEW}
              defaultBasemap="street"
              navigationPosition="top-left"
              interactiveLayerIds={['ih-cell-fill', 'ih-cell-dot']}
              onLoad={(e) => {
                mapRef.current = e.target;
                fit();
              }}
              onClick={onMapClick}
              onMouseMove={onMouseMove}
              onMouseLeave={() => setHover(null)}
              overlay={
                <>
                  <div className="absolute top-3 right-3 z-[1000]">
                    <button
                      type="button"
                      onClick={fit}
                      className="bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium text-slate-700"
                    >
                      Fit
                    </button>
                  </div>
                  {hoveredOrSelected && (
                    <div
                      className="absolute top-3 left-14 z-[1000] bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-2 text-xs text-slate-700 tabular-nums"
                      data-testid="cell-readout"
                    >
                      <span className="font-semibold text-slate-900">{hours(hoveredOrSelected.idle_h)} h idle</span>
                      {' · '}
                      {num(hoveredOrSelected.visits)} visit{hoveredOrSelected.visits === 1 ? '' : 's'}
                      {hoveredOrSelected.zone_name && <span className="block text-slate-500">In {hoveredOrSelected.zone_name}</span>}
                    </div>
                  )}
                </>
              }
            >
              {cellsFC.features.length > 0 && (
                <Source id="ih-cells" type="geojson" data={cellsFC}>
                  <Layer
                    id="ih-cell-fill"
                    type="fill"
                    minzoom={SQUARES_FROM_ZOOM}
                    paint={{ 'fill-color': ['get', 'colour'], 'fill-opacity': 0.8 }}
                  />
                  <Layer
                    id="ih-cell-line"
                    type="line"
                    minzoom={SQUARES_FROM_ZOOM}
                    paint={{ 'line-color': HEAT_OUTLINE, 'line-width': 0.6, 'line-opacity': 0.55 }}
                  />
                </Source>
              )}
              {pointsFC.features.length > 0 && (
                <Source id="ih-points" type="geojson" data={pointsFC}>
                  <Layer
                    id="ih-cell-dot"
                    type="circle"
                    maxzoom={SQUARES_FROM_ZOOM}
                    layout={{ 'circle-sort-key': ['get', 'idle_h'] }}
                    paint={{
                      'circle-color': ['get', 'colour'],
                      'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 8, 4, 10.9, 6.5],
                      'circle-stroke-color': HEAT_OUTLINE,
                      'circle-stroke-width': 0.6,
                      'circle-opacity': 0.9,
                    }}
                  />
                </Source>
              )}
              {badges.map((h) => (
                <Marker key={h.id} longitude={h.lon} latitude={h.lat} anchor="bottom-left" offset={[4, -4]}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      centreOn(h);
                    }}
                    title={`Hotspot ${h.rank}: ${hours(h.idle_h)} h idle`}
                    aria-label={`Hotspot ${h.rank} on the map`}
                    // Fixed ink: the badge sits on the basemap, which does not follow the app theme.
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[11px] font-semibold tabular-nums shadow"
                    style={{
                      background: selected === h.id ? '#0f766e' : '#0f172a',
                      color: '#ffffff',
                      boxShadow: '0 0 0 2px #ffffff, 0 1px 3px rgb(0 0 0 / 0.35)',
                    }}
                  >
                    {h.rank}
                  </button>
                </Marker>
              ))}
              {selectedFC.features.length > 0 && (
                <Source id="ih-selected" type="geojson" data={selectedFC}>
                  <Layer id="ih-selected-halo" type="line" paint={{ 'line-color': '#ffffff', 'line-width': 5 }} />
                  <Layer id="ih-selected-line" type="line" paint={{ 'line-color': '#0f172a', 'line-width': 2.5 }} />
                </Source>
              )}
            </LexiStarMap>
            {loading && (
              <div className="absolute inset-0 z-[1001] flex items-center justify-center bg-surface/40 pointer-events-none">
                <span className="rounded-lg bg-surface/95 px-3 py-1.5 text-xs font-medium text-slate-700 shadow">Loading idle cells…</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1 text-[11px] text-slate-600" aria-label="Idle hours per cell">
            <span className="font-medium text-slate-700">Idle hours per 200 m cell</span>
            <ul className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label="Legend">
              {HEAT_RAMP.map((c, i) => (
                <li key={c} className="inline-flex items-center gap-1.5 tabular-nums">
                  <span className="inline-block w-3.5 h-3.5 rounded-sm" style={{ background: c, boxShadow: `inset 0 0 0 1px ${HEAT_OUTLINE}55` }} />
                  {labels[i]}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className={`${cardClasses} xl:col-span-4 flex flex-col min-w-0 xl:max-h-[560px]`} aria-labelledby="hotspots-title">
          <div className="px-4 pt-4 pb-2">
            <h2 id="hotspots-title" className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <FaFire className="text-slate-400" size={12} aria-hidden="true" />
              Idle hotspots
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Ranked by idle hours. <ProvenanceMark provenance="estimated" /> {s ? estimateNote(s) : ''}
            </p>
          </div>
          <div className="overflow-auto flex-1 min-h-0">
            {loading && !data ? (
              <div className="p-4 space-y-2">
                <SkeletonBlock className="h-8" />
                <SkeletonBlock className="h-8" />
                <SkeletonBlock className="h-8" />
              </div>
            ) : !data || data.hotspots.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-sm font-medium text-slate-900">No idle hotspots</p>
                <p className="text-xs text-slate-600 mt-1">
                  {excludePermitted
                    ? 'No idling outside permitted zones in this period. Include permitted zones to see idle time at sites.'
                    : 'No truck idled in this period with these filters.'}
                </p>
              </div>
            ) : (
              <table className="min-w-full text-xs" aria-label="Idle hotspots">
                <thead className="bg-slate-50 sticky top-0 z-[1]">
                  <tr className="text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap">
                    <th scope="col" className="px-3 py-2 w-6">
                      #
                    </th>
                    <th scope="col" className="px-2 py-2">
                      Where
                    </th>
                    <th scope="col" className="px-2 py-2 text-right">
                      Idle h
                    </th>
                    <th scope="col" className="px-2 py-2 text-right">
                      Visits
                    </th>
                    <th scope="col" className="px-2 py-2 text-right" title="Estimated litres">
                      Est. L
                    </th>
                    <th scope="col" className="px-3 py-2 text-right" title="Estimated cost">
                      Est. R
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.hotspots.map((h) => {
                    const active = selected === h.id;
                    return (
                      <tr
                        key={h.id}
                        className={`cursor-pointer tabular-nums ${active ? 'bg-primary-50' : 'hover:bg-slate-50'}`}
                        onClick={() => centreOn(h)}
                        aria-selected={active}
                      >
                        <td className="px-3 py-2 text-slate-500">{h.rank}</td>
                        <td className="px-2 py-2 min-w-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              centreOn(h);
                            }}
                            className="text-left font-medium text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                            aria-label={`Centre the map on hotspot ${h.rank}, ${h.label}`}
                          >
                            <FaCrosshairs className="inline mr-1 text-slate-400" size={9} aria-hidden="true" />
                            {h.label}
                          </button>
                          {hotspotNote(h) && (
                            <span className="block text-[11px] text-slate-500 font-normal">{hotspotNote(h)}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-slate-900">{hours(h.idle_h)}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{num(h.visits)}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{num(h.est_litres)}</td>
                        <td className="px-3 py-2 text-right text-slate-700">{h.est_rand === null ? '—' : `R${num(h.est_rand)}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

